/**
 * FsNotesCard — Batch #14.320 : notes complémentaires rédigées par le cabinet pour l'exercice (titre + texte),
 * imprimées en dernière page du PDF « États financiers complets » (section 4 + table des matières).
 */
import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, errMsg, inputCls, useGlBase } from "../_shared";
import { Card } from "./_bits";

export default function FsNotesCard({ asOf, fyStart, initial }) {
  const base = useGlBase();
  const [notes, setNotes] = useState(initial || []);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNotes(initial || []); setDirty(false); }, [initial]);

  const edit = (i, k, v) => { setNotes((ns) => ns.map((n, j) => (j === i ? { ...n, [k]: v } : n))); setDirty(true); };
  const add = () => { setNotes((ns) => [...ns, { title: "", body: "" }]); setDirty(true); };
  const remove = (i) => { setNotes((ns) => ns.filter((_, j) => j !== i)); setDirty(true); };
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`${base}/reports/financial-statements/notes`, { notes }, { params: { as_of: asOf } });
      setNotes(r.data.notes); setDirty(false);
      toast.success(r.data.notes.length ? `${r.data.notes.length} note(s) enregistrée(s) — imprimées en dernière page du PDF` : "Notes supprimées");
    } catch (e) { toast.error(errMsg(e, "Enregistrement impossible")); } finally { setBusy(false); }
  };

  return (
    <Card title={`Notes complémentaires — exercice débutant le ${fyStart}`} testId="fs-notes-card"
      right={<span className="flex items-center gap-2">
        <Btn tone="slate" icon={Plus} onClick={add} testId="fs-notes-add">Ajouter une note</Btn>
        <Btn tone="emerald" icon={busy ? Loader2 : Save} busy={busy} disabled={!dirty} onClick={save} testId="fs-notes-save">Enregistrer</Btn>
      </span>}>
      <div className="p-4 space-y-3">
        <p className="text-[11px] text-slate-500">Rédigées par le cabinet (méthodes comptables, événements importants, précisions sur un poste…). Elles paraissent en <strong>dernière page</strong> des états financiers complets et dans la table des matières. Séparez les paragraphes par une ligne vide.</p>
        {notes.length === 0 && <p className="text-xs text-slate-400 italic" data-testid="fs-notes-empty">Aucune note pour cet exercice.</p>}
        {notes.map((n, i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-2 bg-slate-50/50" data-testid={`fs-note-${i}`}>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-500 w-6">{i + 1}.</span>
              <input className={inputCls} value={n.title} onChange={(e) => edit(i, "title", e.target.value)} placeholder="Titre de la note (ex. Méthodes comptables)" data-testid={`fs-note-title-${i}`} />
              <button onClick={() => remove(i)} className="p-2 text-slate-400 hover:text-rose-600" title="Supprimer la note" data-testid={`fs-note-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="pl-8"><textarea className={`${inputCls} min-h-[80px]`} value={n.body} onChange={(e) => edit(i, "body", e.target.value)} placeholder="Texte de la note…" data-testid={`fs-note-body-${i}`} /></div>
          </div>
        ))}
      </div>
    </Card>
  );
}
